import type { SequenceResponse } from "@/lib/api/model/sequenceResponse";

import {
  ALL_DAYS,
  type WizardDetailsValues,
  type WizardRulesValues,
  type WizardScheduleValues,
} from "../store/wizard-store";

const DEFAULT_WINDOWS: WizardScheduleValues["windows"] = ALL_DAYS.map(
  (day) => ({
    day,
    enabled: day !== "sat" && day !== "sun",
    start: "09:00",
    end: "17:00",
  }),
);

/** Pre-fill the create wizard from a source sequence for the clone flow. */
export interface SequenceWizardPrefill {
  details: WizardDetailsValues;
  schedule: WizardScheduleValues;
  rules: WizardRulesValues;
}

/**
 * Map a fully-loaded source sequence into the wizard's three value slices so
 * the clone flow opens pre-filled. The name is prefixed with "Copy of " (the
 * user can edit it); sender, mailbox, schedule and rules carry over verbatim
 * and stay editable. Steps are NOT represented here — the backend copies them
 * server-side when the clone is submitted.
 */
export function sequenceToWizardPrefill(
  source: SequenceResponse,
): SequenceWizardPrefill {
  const schedule = source.schedule ?? {};
  const sourceWindows = schedule.windows ?? null;
  const windows = sourceWindows
    ? ALL_DAYS.map((day) => {
        const w = sourceWindows.find((x) => x.day === day);
        return w
          ? {
              day,
              enabled: w.enabled,
              start: w.start ?? null,
              end: w.end ?? null,
            }
          : { day, enabled: false, start: null, end: null };
      })
    : DEFAULT_WINDOWS.map((w) => ({ ...w }));

  const rules = source.rules ?? {};

  return {
    details: {
      name: `Copy of ${source.name}`.slice(0, 255),
      sender_user_id: source.sender_user_id ?? null,
      mailbox_id: source.mailbox_id ?? null,
    },
    schedule: {
      timezone: schedule.timezone ?? "America/New_York",
      windows,
      skip_holidays: schedule.skip_holidays ?? true,
    },
    rules: {
      pause_on_out_of_office: rules.pause_on_out_of_office ?? true,
      interested_action: rules.interested_action ?? "do_nothing",
      interested_stage_id: rules.interested_stage_id ?? null,
      interested_auto_draft_enabled: rules.interested_auto_draft_enabled ?? false,
      interested_auto_draft_instructions:
        rules.interested_auto_draft_instructions ?? "",
      not_interested_action: rules.not_interested_action ?? "do_nothing",
      not_interested_stage_id: rules.not_interested_stage_id ?? null,
      not_interested_auto_draft_enabled:
        rules.not_interested_auto_draft_enabled ?? false,
      not_interested_auto_draft_instructions:
        rules.not_interested_auto_draft_instructions ?? "",
      cc_emails: rules.cc_emails ?? [],
      bcc_emails: rules.bcc_emails ?? [],
    },
  };
}
